import { useEffect, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "@/lib/error-message";
import { QueryError } from "@/components/Common/QueryError";
import { Save, Mail } from "lucide-react";
import { useRecipients, useUpdateRecipients } from "./queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const FIELDS: { key: "contact" | "dealer" | "jobs"; label: string; hint: string }[] = [
  {
    key: "contact",
    label: "İletişim Formu Alıcıları",
    hint: "İletişim formu mesajları bu adres(ler)e gönderilir.",
  },
  {
    key: "dealer",
    label: "Bayi Başvurusu Alıcıları",
    hint: "Bayi / iş ortağı başvuruları bu adres(ler)e gönderilir.",
  },
  {
    key: "jobs",
    label: "İş Başvurusu Alıcıları",
    hint: "İş başvuruları (CV ekli) bu adres(ler)e gönderilir.",
  },
];

export default function SettingsPage() {
  const { data, isLoading, isError, error, refetch } = useRecipients();
  const update = useUpdateRecipients();
  const [form, setForm] = useState({ contact: "", dealer: "", jobs: "" });

  useEffect(() => {
    if (data) setForm({ contact: data.contact, dealer: data.dealer, jobs: data.jobs });
  }, [data]);

  async function handleSave() {
    try {
      await update.mutateAsync(form);
      toast.success("Alıcı adresleri kaydedildi.");
    } catch (e) {
      toast.error(errorMessage(e, "Kaydedilemedi. Lütfen tekrar deneyin."));
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Ayarlar</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Form bildirimlerinin gönderileceği e-posta adreslerini yönetin.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5" /> Form Bildirim Alıcıları
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </>
          ) : isError && !data ? (
            // Veri gelmeden boş formu kaydetmek mevcut adresleri silerdi — formu gösterme.
            <QueryError error={error} onRetry={() => void refetch()} />
          ) : (
            <>
              {FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    type="text"
                    placeholder="ornek@firma.com, ikinci@firma.com"
                    value={form[f.key]}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">{f.hint}</p>
                </div>
              ))}
              <p className="text-xs text-muted-foreground border-t pt-3">
                Birden fazla alıcı için adresleri <strong>virgülle</strong> ayırın. Boş bırakılırsa
                sunucu ortam değişkenindeki (<code>MAIL_TO_*</code>) varsayılan adres kullanılır.
              </p>
              <Button onClick={handleSave} disabled={update.isPending} className="gap-2">
                <Save className="w-4 h-4" />
                {update.isPending ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
